const PREFIX = 'apie:';

const SENSITIVE_KEYS = new Set([
  'userWallet', 'merchantWallet', 'walletAddress', 'merchantWalletAddress',
  'escrowAddress', 'btcWalletAddress', 'pubkey',
  'psbt', 'signedPsbt', 'signedPsbtUser', 'signedPsbtMerchant',
  'signedPsbtAdmin', 'releasePsbt', 'resolvePsbt',
  'chatMessage',
  // alias keys used in transaction lists
  'userPubkey', 'merchantPubkey', 'adminPubkey',
  'userBtcAddress', 'merchantBtcAddress', 'adminBtcAddress',
  'adminWalletAddress', 'adminBtcWalletAddress', 'adminPub',
  // escrow pubkey fields
  'userPub', 'merchantPub',
  // payment method fields
  'accountNumber', 'accountHolderName', 'iban', 'bicSwiftCode',
  'upiId', 'linkedPhoneNumber',
  'cardNumber', 'cardHolderName', 'expiryDate',
  'appleIdEmail', 'bankName',
  // transaction fields
  'apikey', 'chainId', 'txHash', 'referenceId',
  // escrow fields
  'signature',
  'recaptchaToken',
]);

function hexToBytes(hex) {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}

function toHex(buf) {
  return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
}

let _cachedKey = null;
async function getCryptoKey() {
  if (_cachedKey) return _cachedKey;
  const hex = import.meta.env.VITE_API_ENCRYPT_KEY;
  if (!hex || hex.length !== 64) return null;
  _cachedKey = await crypto.subtle.importKey(
    'raw',
    hexToBytes(hex),
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
  return _cachedKey;
}

export async function apiEncrypt(plain) {
  if (!plain || typeof plain !== 'string') return plain;
  try {
    const key = await getCryptoKey();
    if (!key) return plain;
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      new TextEncoder().encode(plain)
    );
    const enc = new Uint8Array(encrypted);
    const ciphertext = enc.slice(0, -16);
    const tag = enc.slice(-16);
    return `${PREFIX}${toHex(iv)}:${toHex(tag)}:${toHex(ciphertext)}`;
  } catch {
    return plain;
  }
}

export async function apiDecrypt(stored) {
  if (!stored || typeof stored !== 'string' || !stored.startsWith(PREFIX)) return stored;
  try {
    const key = await getCryptoKey();
    if (!key) return stored;
    const parts = stored.slice(PREFIX.length).split(':');
    if (parts.length !== 3) return stored;
    const [ivHex, tagHex, encHex] = parts;
    const iv = hexToBytes(ivHex);
    const tag = hexToBytes(tagHex);
    const enc = hexToBytes(encHex);
    const combined = new Uint8Array(enc.length + tag.length);
    combined.set(enc);
    combined.set(tag, enc.length);
    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      key,
      combined
    );
    return new TextDecoder().decode(decrypted);
  } catch {
    return stored;
  }
}

export async function encryptSensitiveFields(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return Promise.all(obj.map(encryptSensitiveFields));
  const result = { ...obj };
  for (const [k, v] of Object.entries(result)) {
    if (SENSITIVE_KEYS.has(k) && (typeof v === 'string' || typeof v === 'number') && v !== null && v !== '') {
      const strVal = String(v);
      if (!strVal.startsWith(PREFIX)) {
        result[k] = await apiEncrypt(strVal);
      }
    } else if (v && typeof v === 'object') {
      result[k] = await encryptSensitiveFields(v);
    }
  }
  return result;
}

export async function decryptResponseFields(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return Promise.all(obj.map(decryptResponseFields));
  const result = { ...obj };
  for (const [k, v] of Object.entries(result)) {
    if (typeof v === 'string' && v.startsWith(PREFIX)) {
      result[k] = await apiDecrypt(v);
    } else if (v && typeof v === 'object') {
      result[k] = await decryptResponseFields(v);
    }
  }
  return result;
}
